import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import './WarehouseDashboard.css';

const WarehouseLocationsList = forwardRef(({ onSelectLocation, selectedLocationId, onNew }, ref) => {
  const { t } = useTranslation('warehouse');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchLocations
  }));

  useEffect(() => {
    fetchLocations();
  }, [statusFilter]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/warehouse-locations?limit=500');
      if (statusFilter !== 'all') {
        url += `&is_active=${statusFilter === 'active'}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else {
        setError(t('warehouseLocations.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching warehouse locations:', error);
      setError(t('warehouseLocations.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = locations.filter(loc => {
    const matchesSearch = (
      (loc.location_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (loc.location_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (loc.zone || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesType = typeFilter === 'all' || loc.location_type === typeFilter;

    return matchesSearch && matchesType;
  });

  // Get unique zones for filter dropdown
  const uniqueZones = [...new Set(locations.map(loc => loc.zone).filter(Boolean))].sort();

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('common.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('common.status.inactive')}</span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && locations.length === 0) {
    return <div className="list-container"><div className="loading">{t('warehouseLocations.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('warehouseLocations.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('warehouseLocations.newLocation')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('warehouseLocations.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('warehouseLocations.filters.allStatus')}</option>
            <option value="active">{t('common.status.active')}</option>
            <option value="inactive">{t('common.status.inactive')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('warehouseLocations.filters.allTypes')}</option>
            <option value="BULK">{t('warehouseLocations.view.types.BULK')}</option>
            <option value="PALLET">{t('warehouseLocations.view.types.PALLET')}</option>
            <option value="SHELF">{t('warehouseLocations.view.types.SHELF')}</option>
            <option value="SECURE">{t('warehouseLocations.view.types.SECURE')}</option>
            <option value="QUARANTINE">{t('warehouseLocations.view.types.QUARANTINE')}</option>
            <option value="STAGING">{t('warehouseLocations.view.types.STAGING')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('warehouseLocations.tableHeaders.code')}</th>
              <th>{t('warehouseLocations.tableHeaders.name')}</th>
              <th>{t('warehouseLocations.tableHeaders.zone')}</th>
              <th>{t('warehouseLocations.tableHeaders.type')}</th>
              <th>{t('warehouseLocations.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredLocations.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm || typeFilter !== 'all' ? t('warehouseLocations.noResults') : t('warehouseLocations.noData')}
                </td>
              </tr>
            ) : (
              filteredLocations.map(loc => (
                <tr
                  key={loc.location_id}
                  onClick={() => onSelectLocation(loc.location_id)}
                  className={selectedLocationId === loc.location_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">{loc.location_code}</span>
                  </td>
                  <td>
                    <strong>{loc.location_name || '-'}</strong>
                  </td>
                  <td>{loc.zone || '-'}</td>
                  <td>{t(`warehouseLocations.view.types.${loc.location_type}`, loc.location_type)}</td>
                  <td>
                    {getStatusBadge(loc.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default WarehouseLocationsList;
